import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, BadgeCheck, Lock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { BdFlag } from "../../components/BdFlag";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

export default function PhoneVerifyPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const alreadyVerified = !!user?.phone_verified;
  const [step, setStep] = useState(user?.phone ? "code" : "phone");
  const [phone, setPhone] = useState(user?.phone || "");
  // Country code is LOCKED to Bangladesh (+880). Backend still accepts the
  // field for compatibility, but the user can never change it here.
  const countryCode = "+880";
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // Live availability check (Binance/Bybit-style)
  const [phoneCheck, setPhoneCheck] = useState({ status: "idle", reason: "" });
  const phoneAbortRef = useRef(null);

  useEffect(() => {
    const raw = (phone || "").replace(/[\s-]/g, "");
    const own = (user?.phone || "").replace(/[\s-]/g, "");
    if (!raw || raw === own) {
      setPhoneCheck({ status: "idle", reason: "" });
      return undefined;
    }
    setPhoneCheck({ status: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        if (phoneAbortRef.current) phoneAbortRef.current.abort();
        const ctrl = new AbortController();
        phoneAbortRef.current = ctrl;
        const res = await fetch(
          `${API_BASE}/auth/check-phone?phone=${encodeURIComponent(raw)}&country_code=${encodeURIComponent(countryCode)}`,
          { signal: ctrl.signal },
        );
        const data = await res.json();
        if (data && data.available) {
          setPhoneCheck({ status: "available", reason: "" });
        } else {
          const r = (data && data.reason) || "Not available";
          const isTaken = /registered|taken|already/i.test(r);
          setPhoneCheck({ status: isTaken ? "taken" : "invalid", reason: r });
        }
      } catch (e) {
        if (e && e.name === "AbortError") return;
        setPhoneCheck({ status: "idle", reason: "" });
      }
    }, 420);
    return () => clearTimeout(t);
  }, [phone, user?.phone]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = async (e) => {
    e?.preventDefault();
    const cleaned = phone.replace(/[\s-]/g, "");
    if (!cleaned || !/^\d{6,15}$/.test(cleaned)) {
      toast.error("Enter a valid phone number (6–15 digits)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/phone/send-code", {
        phone: cleaned,
        country_code: countryCode,
      });
      if (data?.dev_code) {
        toast.success(`Code sent! (Dev: ${data.dev_code})`, { duration: 8000 });
      } else {
        toast.success("Verification code sent to your phone");
      }
      setStep("code");
      setResendIn(45);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send code");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length < 4) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/phone/verify", { phone: phone.replace(/[\s-]/g, ""), code });
      await refreshUser();
      toast.success("Phone verified!");
      navigate("/profile/verification", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyVerified) {
    return (
      <ProfileShell title="Phone">
        <div className="py-2 pb-10 flex flex-col items-center" data-testid="phone-locked">
          <div className="pt-4 pb-6">
            <div
              className="h-32 w-32 rounded-2xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, rgba(25,165,92,0.18) 0%, rgba(25,165,92,0.04) 70%)",
                boxShadow: "inset 0 0 0 1px rgba(33,196,110,0.4), 0 12px 30px rgba(25,165,92,0.25)",
              }}
            >
              <BadgeCheck size={56} className="text-[#21c46e]" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-[26px] font-extrabold text-white text-center">Phone verified</h2>
          <p className="text-white/55 text-sm sm:text-[15px] text-center mt-2 max-w-sm leading-relaxed">
            Your phone number <b className="text-white">{user.country_code} {user.phone}</b> is verified and locked. For security, it cannot be changed.
          </p>
          <p className="text-white/45 text-xs text-center mt-3 max-w-sm">
            Need to update your phone? Please contact support — they will help after additional identity checks.
          </p>
          <button
            type="button"
            onClick={() => navigate("/profile/verification")}
            data-testid="back-to-verification"
            className="mt-6 h-11 px-5 rounded-lg bg-white/[0.06] border border-white/10 text-white/85 hover:bg-white/[0.10]"
          >
            Back to verification
          </button>
        </div>
      </ProfileShell>
    );
  }

  return (
    <ProfileShell title="Phone">
      <div className="py-2 pb-10 flex flex-col items-center" data-testid="phone-verify-page">
        {/* Hero illustration */}
        <div className="pt-4 pb-6 flex items-center justify-center">
          <div
            className="h-32 w-32 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(33,196,110,0.18) 0%, rgba(33,196,110,0.04) 70%)",
              boxShadow: "inset 0 0 0 1px rgba(33,196,110,0.25), 0 12px 30px rgba(25,165,92,0.25)",
            }}
          >
            <Phone size={56} className="text-[#21c46e]" />
          </div>
        </div>

        <h2 className="text-2xl sm:text-[26px] font-extrabold text-white text-center">Phone number</h2>
        <p className="text-white/55 text-sm sm:text-[15px] text-center mt-2 max-w-sm leading-relaxed">
          A 6-digit code will be sent via SMS to verify your number. Once verified, it cannot be changed.
        </p>

        <div className="w-full max-w-md mt-8 space-y-4">
          {step === "phone" && (
            <form onSubmit={sendCode} className="space-y-4" data-testid="phone-form">
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <div className="block">
                  <span className="text-xs uppercase tracking-wider text-white/55">Code</span>
                  <div
                    data-testid="phone-cc-locked"
                    className="mt-1 w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-3 flex items-center gap-2 cursor-not-allowed"
                    title="Country code locked to Bangladesh"
                  >
                    <BdFlag size={20} />
                    <span className="text-white font-semibold">{countryCode}</span>
                    <Lock size={12} className="ml-auto text-white/40" />
                  </div>
                </div>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-white/55">Phone number</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    data-testid="phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 11))}
                    placeholder="01712345678"
                    maxLength={11}
                    className="mt-1 w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-4 text-white tabular-nums focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25"
                  />
                </label>
              </div>
              <button
                type="submit"
                data-testid="phone-send-code"
                disabled={submitting}
                className="w-full h-12 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                style={{
                  background: "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                  boxShadow: "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
              >
                {submitting ? "Sending…" : "Send code"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="phone-code-form">
              <div className="flex items-center justify-center gap-2 text-white/65 text-sm">
                <Phone size={15} className="text-[#21c46e]" />
                <span className="truncate">{countryCode} {phone}</span>
                <button
                  type="button"
                  onClick={() => setStep("phone")}
                  className="text-[#21c46e] text-xs font-semibold hover:underline"
                >
                  Change
                </button>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-white/55">Verification code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="phone-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="mt-1 w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-4 text-white text-center text-xl font-bold tracking-[0.6em] placeholder:text-white/20 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25"
                />
              </label>
              <button
                type="submit"
                data-testid="phone-code-verify"
                disabled={submitting}
                className="w-full h-12 rounded-lg text-white font-bold disabled:opacity-60"
                style={{
                  background: "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                  boxShadow: "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
              >
                {submitting ? "Verifying…" : "Verify"}
              </button>
              <button
                type="button"
                disabled={resendIn > 0 || submitting}
                onClick={() => sendCode()}
                data-testid="phone-code-resend"
                className="w-full h-10 text-white/55 hover:text-white text-sm disabled:opacity-50"
              >
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
            </form>
          )}
        </div>
      </div>
    </ProfileShell>
  );
}
